<template>
  <div v-if="isOpen" class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-4xl bg-gray-800 text-gray-300 rounded-lg p-5 sm:p-6 flex flex-col gap-4 relative shadow-lg border border-gray-700 animate-scaleIn">
      <!-- Header -->
      <div class="flex justify-between items-center border-b border-gray-700 pb-3">
        <div class="flex items-center gap-3">
          <i class="fas fa-id-badge text-indigo-300 text-2xl"></i>
          <h2 class="text-lg sm:text-xl font-semibold text-indigo-300">ฉายาของฉัน</h2>
        </div>
        <button
          @click="closeModal"
          class="text-gray-300 hover:text-indigo-300 transition-colors duration-200"
          :disabled="isProcessing"
          aria-label="ปิด"
        >
          <i class="fas fa-times text-base"></i>
        </button>
      </div>

      <!-- Top summary chips -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div class="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm flex items-center justify-between">
          <span class="text-indigo-200">เลเวล</span>
          <span class="font-semibold text-white">{{ level }}</span>
        </div>
        <div class="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm flex items-center justify-between">
          <span class="text-indigo-200">ชื่อเสียง</span>
          <span class="font-semibold text-white">{{ formatNumber(renown) }}</span>
        </div>
        <div class="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm flex items-center justify-between">
          <span class="text-indigo-200">ทอง</span>
          <span class="font-semibold text-white">{{ formatNumber(gold) }}</span>
        </div>
        <div class="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm flex items-center justify-between">
          <span class="text-indigo-200">เหรียญ</span>
          <span class="font-semibold text-white">{{ formatNumber(silver) }}</span>
        </div>
      </div>

      <!-- Active title badge -->
      <div v-if="activeTitle" class="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm flex items-center gap-2">
        <i class="fas fa-crown text-yellow-400"></i>
        <span class="text-indigo-200">ฉายาที่ใช้งานอยู่:</span>
        <span class="font-semibold text-white">{{ activeTitle }}</span>
        <span class="ml-auto text-xs text-green-400">+{{ titleStats.stamina || 0 }} STA · +{{ titleStats.str || 0 }} STR · +{{ titleStats.int || 0 }} INT · +{{ titleStats.agi || 0 }} AGI</span>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-10">
        <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
      </div>

      <!-- List -->
      <div v-else class="max-h-80 overflow-y-auto" ref="titleList" id="style-1">
        <div
          v-for="title in titles"
          :key="title.name"
          @click="toggleTitleAction(title)"
          class="rounded-md p-3 sm:p-4 mb-2 border transition-all duration-200 cursor-pointer bg-gray-800 hover:bg-gray-700"
          :class="[
            unlockTitles.includes(title.name) ? 'border-indigo-700' : 'border-gray-700',
            selectedTitle === title.name ? 'ring-2 ring-indigo-700' : '',
            activeTitle === title.name ? 'bg-gray-700' : ''
          ]"
          :data-title="title.name"
          :aria-selected="selectedTitle === title.name"
          tabindex="0"
          @keydown.enter="toggleTitleAction(title)"
          @keydown.space="toggleTitleAction(title)"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="flex items-center gap-2">
                <h3 class="font-semibold text-white truncate">{{ title.name }}</h3>
                <span
                  v-if="activeTitle === title.name"
                  class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-indigo-800 border border-indigo-900 text-white"
                >
                  <i class="fas fa-check"></i> ใช้งานอยู่
                </span>
                <span
                  v-else-if="unlockTitles.includes(title.name)"
                  class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-gray-800 border border-gray-700 text-indigo-200"
                >
                  <i class="fas fa-unlock"></i> ปลดล็อกแล้ว
                </span>
                <span
                  v-else
                  class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-gray-800 border border-gray-700 text-indigo-200"
                >
                  <i class="fas fa-lock"></i> ยังไม่ปลดล็อก
                </span>
              </div>

              <p class="text-xs text-indigo-200 mt-1">
                {{ getEffectText(title.stats) }}
              </p>

              <div class="flex flex-wrap gap-2 mt-2">
                <span class="chip">เลเวล {{ title.levelRequired }}</span>
                <span class="chip">ชื่อเสียง {{ formatNumber(title.renown) }}</span>
                <span class="chip">ทอง {{ formatNumber(title.goldCost) }}</span>
                <span class="chip">เหรียญ {{ formatNumber(title.silverCost) }}</span>
                <span v-if="title.requiredTitle" class="chip">ต้องมี {{ title.requiredTitle }}</span>
              </div>
            </div>

            <div class="flex flex-col items-end gap-1">
              <i
                v-if="unlockTitles.includes(title.name)"
                class="fas fa-check-circle text-green-400 text-lg"
                aria-hidden="true"
              ></i>
              <i
                v-else
                class="fas fa-circle text-gray-600 text-xs"
                aria-hidden="true"
              ></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div v-if="!loading && selectedTitle" class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <!-- Unlock -->
        <button
          v-if="!unlockTitles.includes(selectedTitle) && canUnlock(selectedTitleData)"
          @click="unlockTitle(selectedTitleData)"
          class="btn-primary"
          :disabled="isProcessing || !canAfford(selectedTitleData)"
          aria-label="ปลดล็อกฉายา"
        >
          <span v-if="!isProcessing"><i class="fas fa-unlock mr-2 text-xs"></i>ปลดล็อกฉายา</span>
          <div v-else class="spinner-sm"></div>
        </button>

        <!-- Use -->
        <button
          v-else-if="unlockTitles.includes(selectedTitle) && activeTitle !== selectedTitle"
          @click="useTitle(selectedTitleData)"
          class="btn-primary"
          :disabled="isProcessing"
          aria-label="ใช้งานฉายา"
        >
          <span v-if="!isProcessing"><i class="fas fa-check mr-2 text-xs"></i>ใช้งานฉายา</span>
          <div v-else class="spinner-sm"></div>
        </button>

        <!-- Already active -->
        <button
          v-else
          disabled
          class="btn-disabled"
        >
          ใช้งานอยู่แล้ว
        </button>
      </div>

      <!-- Error / Info -->
      <div v-if="errorMessage" class="text-red-300 text-xs text-center">
        {{ errorMessage }}
      </div>
      <div v-else-if="selectedTitle && !unlockTitles.includes(selectedTitle) && !canUnlock(selectedTitleData)" class="text-yellow-300 text-xs text-center">
        ต้องปลดล็อก “{{ selectedTitleData.requiredTitle }}” ก่อน
      </div>
      <div v-else-if="selectedTitle && !unlockTitles.includes(selectedTitle) && canUnlock(selectedTitleData) && !canAfford(selectedTitleData)" class="text-yellow-300 text-xs text-center">
        ทรัพยากรไม่เพียงพอ
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed, nextTick } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';

export default {
  name: 'TitlesModal',
  props: {
    isOpen: { type: Boolean, default: false }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();

    const renown = ref(0);
    const gold = ref(0);
    const silver = ref(0);
    const level = ref(0);

    const unlockTitles = ref([]);
    const titleStats = ref({ str: 0, stamina: 0, int: 0, agi: 0 });
    const activeTitle = ref('');

    const errorMessage = ref('');
    const loading = ref(true);
    const isProcessing = ref(false);

    const titleList = ref(null);
    const selectedTitle = ref(null);

    const titles = ref([
      { name: 'เด็กเก็บขยะ', stats: { stamina: 3, str: 0, int: 0, agi: 0 }, renown: 300,  goldCost: 300,  silverCost: 3000,  levelRequired: 3 },
      { name: 'เด็กส่งของ', stats: { stamina: 3, str: 3, int: 0, agi: 0 }, renown: 600,  goldCost: 600,  silverCost: 6000,  levelRequired: 6,  requiredTitle: 'เด็กเก็บขยะ' },
      { name: 'คนรับใช้', stats: { stamina: 3, str: 3, int: 3, agi: 0 }, renown: 900,  goldCost: 900,  silverCost: 9000,  levelRequired: 9,  requiredTitle: 'เด็กส่งของ' },
      { name: 'คนสอดรู้', stats: { stamina: 3, str: 3, int: 3, agi: 3 }, renown: 1200, goldCost: 1200, silverCost: 12000, levelRequired: 12, requiredTitle: 'คนรับใช้' },
      { name: 'ช่างซ่อม', stats: { stamina: 6, str: 3, int: 3, agi: 3 }, renown: 1500, goldCost: 1500, silverCost: 15000, levelRequired: 15, requiredTitle: 'คนสอดรู้' },
      { name: 'นักสำรวจ', stats: { stamina: 6, str: 6, int: 3, agi: 3 }, renown: 1800, goldCost: 1800, silverCost: 18000, levelRequired: 18, requiredTitle: 'ช่างซ่อม' },
      { name: 'นักวางแผน', stats: { stamina: 6, str: 6, int: 6, agi: 6 }, renown: 2100, goldCost: 2100, silverCost: 21000, levelRequired: 21, requiredTitle: 'นักสำรวจ' },
      { name: 'ผู้มีประสบการณ์', stats: { stamina: 9, str: 6, int: 6, agi: 6 }, renown: 2400, goldCost: 2400, silverCost: 24000, levelRequired: 24, requiredTitle: 'นักวางแผน' },
      { name: 'ผู้มีอิทธิพล', stats: { stamina: 9, str: 9, int: 9, agi: 6 }, renown: 2700, goldCost: 2700, silverCost: 27000, levelRequired: 27, requiredTitle: 'ผู้มีประสบการณ์' },
      { name: 'เจ้าพ่อ', stats: { stamina: 10, str: 10, int: 10, agi: 10 }, renown: 3000, goldCost: 3000, silverCost: 30000, levelRequired: 30, requiredTitle: 'ผู้มีอิทธิพล' },
      { name: 'คนครึ่งหุ่นยนต์', stats: { stamina: 13, str: 10, int: 10, agi: 10 }, renown: 3300, goldCost: 3500, silverCost: 40000, levelRequired: 30, requiredTitle: 'เจ้าพ่อ' },
      { name: 'คนเล่นของ', stats: { stamina: 13, str: 13, int: 10, agi: 10 }, renown: 3600, goldCost: 4000, silverCost: 50000, levelRequired: 30, requiredTitle: 'คนครึ่งหุ่นยนต์' },
      { name: 'คนประหลาด', stats: { stamina: 13, str: 13, int: 13, agi: 10 }, renown: 3900, goldCost: 4500, silverCost: 60000, levelRequired: 30, requiredTitle: 'คนเล่นของ' },
      { name: 'มนุษย์เหล็ก', stats: { stamina: 13, str: 13, int: 13, agi: 13 }, renown: 4200, goldCost: 5000, silverCost: 70000, levelRequired: 30, requiredTitle: 'คนประหลาด' },
      { name: 'นักรบเหล็ก', stats: { stamina: 16, str: 13, int: 13, agi: 13 }, renown: 4500, goldCost: 5500, silverCost: 80000, levelRequired: 30, requiredTitle: 'มนุษย์เหล็ก' },
      { name: 'ผู้ทรงพลัง', stats: { stamina: 16, str: 16, int: 13, agi: 13 }, renown: 4800, goldCost: 6000, silverCost: 90000, levelRequired: 30, requiredTitle: 'นักรบเหล็ก' }
    ]);

    const selectedTitleData = computed(() => titles.value.find(t => t.name === selectedTitle.value) || {});

    const getEffectText = (stats) => {
      const list = [];
      if (stats?.stamina > 0) list.push(`+${stats.stamina} Stamina`);
      if (stats?.str > 0)     list.push(`+${stats.str} STR`);
      if (stats?.int > 0)     list.push(`+${stats.int} INT`);
      if (stats?.agi > 0)     list.push(`+${stats.agi} AGI`);
      return list.join(', ');
    };

    const formatNumber = (n) => {
      const num = Number(n);
      if (!Number.isFinite(num)) return '0';
      try { return num.toLocaleString('th-TH'); } catch { return String(num); }
    };

    const scrollToActiveTitle = () => {
      if (!titleList.value || !activeTitle.value) return;
      nextTick(() => {
        const el = titleList.value.querySelector(`[data-title="${activeTitle.value}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    };

    const fetchData = async () => {
      if (!user.value) {
        errorMessage.value = 'กรุณาล็อกอินก่อน';
        loading.value = false;
        return;
      }
      try {
        const charData = playerStore.characterData;
        const charPrivateData = playerStore.characterPrivateData;

        if (charData) {
          const c = charData;
          renown.value = c.renown || 0;
          gold.value   = c.gold   || 0;
          silver.value = c.silver || 0;
          level.value  = c.level  || 0;
          activeTitle.value = c.title || '';
        } else {
          errorMessage.value = 'ไม่พบข้อมูลตัวละคร';
        }

        if (charPrivateData) {
          const p = charPrivateData;
          unlockTitles.value = p.unlockTitles || [];
          titleStats.value   = p.titleStats   || { str: 0, stamina: 0, int: 0, agi: 0 };
        } else {
          errorMessage.value = 'ไม่พบข้อมูลส่วนตัวตัวละคร';
        }

        // Sort titles: unlocked first, then by level/cost
        titles.value = titles.value
          .slice()
          .sort((a, b) => {
            const aUnlocked = unlockTitles.value.includes(a.name);
            const bUnlocked = unlockTitles.value.includes(b.name);
            if (aUnlocked !== bUnlocked) return aUnlocked ? -1 : 1;
            return a.levelRequired - b.levelRequired;
          });

        scrollToActiveTitle();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('fetchData error:', err);
        errorMessage.value = 'ไม่สามารถโหลดข้อมูลได้';
      } finally {
        loading.value = false;
      }
    };

    const canUnlock = (title) => !title.requiredTitle || unlockTitles.value.includes(title.requiredTitle);

    const canAfford = (title) => {
      return renown.value >= (title.renown || 0) &&
             gold.value   >= (title.goldCost || 0) &&
             silver.value >= (title.silverCost || 0) &&
             level.value  >= (title.levelRequired || 0);
    };

    const toggleTitleAction = (title) => {
      selectedTitle.value = selectedTitle.value === title.name ? null : title.name;
    };

    const unlockTitle = async (title) => {
      if (isProcessing.value) return;

      if (!canUnlock(title)) {
        errorMessage.value = 'ต้องปลดล็อกฉายาก่อนหน้าก่อน';
        return;
      }
      if (!canAfford(title)) {
        errorMessage.value = 'ทรัพยากรหรือเลเวลไม่เพียงพอ';
        return;
      }

      isProcessing.value = true;
      errorMessage.value = '';
      try {
        const fn = projectFunctions.httpsCallable('gameActions');
        const res = await fn({ action: 'characterActions', subAction: 'titleActions', titleName: title.name, titleSubAction: 'unlock' });

        gold.value        = res.data.newGold;
        silver.value      = res.data.newSilver;
        unlockTitles.value = res.data.newUnlockTitles;
        selectedTitle.value = null;

        // Update title list to move newly unlocked title to the unlocked group
        titles.value = titles.value
          .slice()
          .sort((a, b) => {
            const aUnlocked = unlockTitles.value.includes(a.name);
            const bUnlocked = unlockTitles.value.includes(b.name);
            if (aUnlocked !== bUnlocked) return aUnlocked ? -1 : 1;
            return a.levelRequired - b.levelRequired;
          });

      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('unlockTitle error:', err);
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการปลดล็อก';
      } finally {
        isProcessing.value = false;
      }
    };

    const useTitle = async (title) => {
      if (isProcessing.value) return;

      isProcessing.value = true;
      errorMessage.value = '';
      try {
        const fn = projectFunctions.httpsCallable('gameActions');
        const res = await fn({ action: 'characterActions', subAction: 'titleActions', titleName: title.name, titleSubAction: 'use' });

        titleStats.value = res.data.newTitleStats;
        activeTitle.value = title.name;
        selectedTitle.value = null;

        scrollToActiveTitle();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('useTitle error:', err);
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการใช้งานฉายา';
      } finally {
        isProcessing.value = false;
      }
    };

    const closeModal = () => {
      if (isProcessing.value) return;
      selectedTitle.value = null;
      errorMessage.value = '';
      emit('close');
    };

    watch(() => props.isOpen, (v) => {
      if (v) {
        loading.value = true;
        fetchData();
      }
    });

    onMounted(() => {
      if (props.isOpen) {
        fetchData();
      }
    });

    return {
      // state
      renown, gold, silver, level,
      unlockTitles, titleStats, activeTitle,
      errorMessage, loading, isProcessing,
      titleList, titles, selectedTitle,

      // computed
      selectedTitleData,

      // methods
      getEffectText, formatNumber, canUnlock, canAfford,
      toggleTitleAction, unlockTitle, useTitle, closeModal
    };
  }
};
</script>

<style scoped>
/* Buttons (ธีมเดียวกับคอมโพเนนต์อื่น ๆ) */
.btn-primary {
  @apply w-full bg-indigo-800 hover:bg-indigo-900 text-white py-2 rounded-md text-sm border border-indigo-900 transition-colors duration-200 flex justify-center items-center;
}
.btn-disabled {
  @apply w-full bg-gray-800 text-gray-300 py-2 rounded-md text-sm border border-gray-700 cursor-not-allowed;
}

/* Spinner (เล็ก) */
.spinner-sm {
  width: 1rem;
  height: 1rem;
  border-top-width: 2px;
  border-right-width: 2px;
  border-color: rgba(255,255,255,0.9);
  border-style: solid;
  border-bottom-color: transparent;
  border-left-color: transparent;
  border-radius: 9999px;
  animation: spin 1s linear infinite;
  margin-left: auto;
  margin-right: auto;
}

/* Chip */
.chip {
  @apply text-2xs sm:text-xs px-2 py-0.5 rounded bg-gray-800 border border-gray-700 text-indigo-200 inline-flex items-center;
}

/* Animations */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.25s ease-out; }

@keyframes scaleIn { from { transform: scale(0.98); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.25s ease-out; }

@keyframes spin { to { transform: rotate(360deg); } }

/* Scrollbar */
#style-1 { scrollbar-width: thin; scrollbar-color: #4b5563 #1f2937; }
#style-1::-webkit-scrollbar { width: 6px; }
#style-1::-webkit-scrollbar-track { background: #1f2937; border-radius: 8px; }
#style-1::-webkit-scrollbar-thumb { background: #374151; border-radius: 8px; }
#style-1::-webkit-scrollbar-thumb:hover { background: #4b5563; }

/* Utilities */
.text-2xs { font-size: 0.7rem; }
</style>